import React, { useEffect, useRef, useState } from "react";
import { Box, Typography } from "../../../design-system";
import PhoneIphoneIcon from "@mui/icons-material/PhoneIphone";
import DesktopWindowsIcon from "@mui/icons-material/DesktopWindows";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import logoSite from "../../../../assets/brand/logo_site.svg";
import DemoVideoPicker from "./DemoVideoPicker";
import { getDemoShareSrc, DEMO_SRC_EVENT } from "./demoSource";

const font = "Outfit, sans-serif";

// Phone preview: render the ET at a true phone viewport (below the ET's 500px xs breakpoint) so
// it shows its genuine mobile layout, then scale it down into a phone-sized frame.
const PHONE_VIEWPORT_W = 390;
const PHONE_FRAME_W = 300;
const PHONE_FRAME_H = 620;
const PHONE_SCALE = PHONE_FRAME_W / PHONE_VIEWPORT_W;
const clamp = (n: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, n));
// Zoom < 1 renders the iframe LARGER (100/scale %) then scales it down — so the full-width
// editor gets more horizontal room and its left checkboxes + right timecode ∓ steppers all
// fit instead of clipping at the box edges (same trick as Letter B).
const DESKTOP_ZOOM_DEFAULT = 0.55;
const iframeStyle = (scale: number): React.CSSProperties => ({
  position: "absolute", top: 0, left: 0, width: `${100 / scale}%`, height: `${100 / scale}%`,
  transform: `scale(${scale})`, transformOrigin: "top left", border: "none", background: "#0b0a0f",
});

/**
 * Variant N — a hero layout whose top-of-page centerpiece IS the live ET demo (instead of an
 * upload box next to the CherryPick copy). A Desktop ⇄ Phone toggle swaps between the full-width
 * editor and a phone-framed mobile view; the "Demo video" picker drives it with any of the
 * logged-in user's library videos (falls back to the Film Reel). Reuses the same /s/ iframe as
 * Letters B and K, so the export/download bar shows inside it too.
 */
const HeroEtPage: React.FC = () => {
  const [mode, setMode] = useState<"desktop" | "phone">("desktop");
  const [shareSrc, setShareSrc] = useState<string>(getDemoShareSrc());
  const [loaded, setLoaded] = useState(false);
  const [inView, setInView] = useState(false);
  const [zoom, setZoom] = useState(DESKTOP_ZOOM_DEFAULT);
  const [fs, setFs] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el || inView) return;
    const io = new IntersectionObserver(
      (es) => { if (es.some((e) => e.isIntersecting)) { setInView(true); io.disconnect(); } },
      { rootMargin: "400px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [inView]);

  useEffect(() => {
    const onSrc = (e: Event) => {
      const next = (e as CustomEvent).detail;
      if (typeof next === "string" && next) { setShareSrc(next); setLoaded(false); }
    };
    window.addEventListener(DEMO_SRC_EVENT, onSrc);
    return () => window.removeEventListener(DEMO_SRC_EVENT, onSrc);
  }, []);

  const src = inView ? shareSrc : "";
  const switchMode = (m: "desktop" | "phone") => { setMode(m); setLoaded(false); };

  const toggleBtn = (m: "desktop" | "phone", label: string, Icon: React.ElementType) => {
    const active = mode === m;
    return (
      <Box
        component="button"
        onClick={() => switchMode(m)}
        sx={{
          display: "inline-flex", alignItems: "center", gap: "6px", cursor: "pointer",
          background: active ? "linear-gradient(100deg,#e61d50,#b3123c)" : "transparent",
          color: active ? "#fff" : "#c2c7d6", border: "none",
          fontFamily: font, fontWeight: 700, fontSize: "13px", px: "16px", py: "8px",
        }}
      >
        <Icon sx={{ fontSize: 16 }} /> {label}
      </Box>
    );
  };

  const loadingOverlay = (
    <Box sx={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "12px", background: "#0b0a0f" }}>
      <img src={logoSite} alt="" width={36} style={{ opacity: 0.85 }} />
      <Typography sx={{ fontFamily: font, fontSize: "13px", color: "#9aa0b4" }}>Loading the live editor…</Typography>
    </Box>
  );

  return (
    <Box ref={ref} sx={{ px: { xs: "16px", md: "32px" }, py: { xs: "24px", md: "40px" }, maxWidth: 1200, mx: "auto" }}>
      {/* hero headline */}
      <Box sx={{ textAlign: "center", mb: "20px" }}>
        <Typography sx={{ fontFamily: font, fontWeight: 800, fontSize: { xs: "30px", md: "44px" }, color: "#fff", letterSpacing: "-0.01em" }}>
          CherryPick your clips — live.
        </Typography>
        <Typography sx={{ fontFamily: font, fontSize: "16px", color: "#c2c7d6", mt: "8px", maxWidth: 720, mx: "auto" }}>
          The real editor, right here. Tick the moments you want, then download one or export a stitched reel — no install, no login.
        </Typography>
      </Box>

      {/* controls: video picker + desktop/phone toggle + (desktop) zoom & full-screen */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 2, flexWrap: "wrap", mb: "16px" }}>
        <DemoVideoPicker />
        <Box sx={{ display: "inline-flex", border: "1px solid #3a2b46", borderRadius: 999, overflow: "hidden" }}>
          {toggleBtn("desktop", "Desktop", DesktopWindowsIcon)}
          {toggleBtn("phone", "Phone", PhoneIphoneIcon)}
        </Box>
        {mode === "desktop" && (
          <>
            <Box sx={{ display: "flex", alignItems: "center", gap: "2px", border: "1px solid rgba(255,255,255,0.14)", borderRadius: "7px", overflow: "hidden" }}>
              <Box component="button" onClick={() => setZoom((z) => clamp(z - 0.1, 0.35, 1.1))} title="Zoom out" sx={zoomBtn}>−</Box>
              <Typography sx={{ fontFamily: font, fontSize: "12px", color: "#c2c7d6", px: "6px", minWidth: 38, textAlign: "center" }}>{Math.round(zoom * 100)}%</Typography>
              <Box component="button" onClick={() => setZoom((z) => clamp(z + 0.1, 0.35, 1.1))} title="Zoom in" sx={zoomBtn}>+</Box>
            </Box>
            <Box component="button" onClick={() => setFs(true)} title="Open full screen" sx={{ cursor: "pointer", display: "inline-flex", alignItems: "center", gap: "5px", background: "transparent", border: "1px solid rgba(255,255,255,0.14)", borderRadius: "7px", color: "#c2c7d6", fontFamily: font, fontSize: "12px", px: "10px", py: "6px", "&:hover": { color: "#fff", borderColor: "rgba(255,255,255,0.3)" } }}>
              <OpenInFullIcon sx={{ fontSize: 14 }} /> Full screen
            </Box>
          </>
        )}
      </Box>

      {/* the live ET — full-width editor OR phone-framed mobile view */}
      {mode === "desktop" ? (
        <Box sx={{ position: "relative", borderRadius: "14px", overflow: "hidden", border: "1px solid rgba(255,255,255,0.12)", background: "#0b0a0f", height: 600, boxShadow: "0 30px 80px -50px rgba(0,0,0,0.8)" }}>
          {src ? (
            <iframe
              title="CherryClipz — live editor"
              src={src}
              allow="fullscreen; clipboard-write"
              onLoad={() => setLoaded(true)}
              style={iframeStyle(zoom)}
            />
          ) : null}
          {!loaded && loadingOverlay}
        </Box>
      ) : (
        <Box sx={{ display: "flex", justifyContent: "center" }}>
          <Box sx={{ position: "relative", width: PHONE_FRAME_W, height: PHONE_FRAME_H, borderRadius: "36px", border: "10px solid #15131b", background: "#0b0a0f", overflow: "hidden", boxShadow: "0 30px 80px -40px rgba(0,0,0,0.9)" }}>
            {src ? (
              <iframe
                title="CherryClipz — mobile view"
                src={src}
                allow="fullscreen; clipboard-write"
                onLoad={() => setLoaded(true)}
                style={{
                  position: "absolute", top: 0, left: 0, border: "none",
                  width: PHONE_VIEWPORT_W, height: Math.round(PHONE_FRAME_H / PHONE_SCALE),
                  transform: `scale(${PHONE_SCALE})`, transformOrigin: "top left",
                }}
              />
            ) : null}
            {!loaded && loadingOverlay}
          </Box>
        </Box>
      )}

      {/* full-screen — scale to fit so the right-edge timecode ∓ steppers are never cut off */}
      {fs && (
        <Box onClick={() => setFs(false)} sx={{ position: "fixed", inset: 0, zIndex: 13000, background: "rgba(4,3,7,0.72)", backdropFilter: "blur(3px)", display: "flex", alignItems: "center", justifyContent: "center", p: { xs: "8px", md: "18px" } }}>
          <Box onClick={(e: React.MouseEvent) => e.stopPropagation()} sx={{ width: "100%", maxWidth: "1500px", height: "92vh", display: "flex", flexDirection: "column", background: "#0b0a0f", border: "1px solid rgba(255,255,255,0.12)", borderRadius: "14px", overflow: "hidden" }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", px: "12px", py: "8px", borderBottom: "1px solid rgba(255,255,255,0.08)", flexShrink: 0 }}>
              <Box component="button" onClick={() => setFs(false)} title="Close" sx={{ cursor: "pointer", background: "transparent", border: "1px solid rgba(255,255,255,0.14)", borderRadius: "7px", color: "#c2c7d6", fontFamily: font, fontSize: "12px", px: "10px", py: "5px", "&:hover": { color: "#fff", borderColor: "rgba(255,255,255,0.3)" } }}>Close ×</Box>
            </Box>
            <Box sx={{ position: "relative", flex: 1, overflow: "hidden" }}>
              {src ? <iframe title="CherryClipz — full editor" src={src} allow="fullscreen; clipboard-write" style={iframeStyle(0.82)} /> : null}
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};

const zoomBtn = { cursor: "pointer", background: "transparent", border: "none", color: "#c2c7d6", fontFamily: font, fontSize: "15px", lineHeight: 1, px: "9px", py: "4px", "&:hover": { color: "#fff", background: "rgba(255,255,255,0.08)" } };

export default HeroEtPage;
